import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import GlassCard from '../GlassCard';
import api, { familiesAPI, membershipsAPI } from '../../services/api';

const UserManagement = () => {
  const { theme } = useTheme();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ full_name: '', is_admin: false });
  const [userFamilies, setUserFamilies] = useState({});
  const [showFamilyAccess, setShowFamilyAccess] = useState(null);
  
  const currentUserId = currentUser?.user?.id || currentUser?.user?.sub || currentUser?.user_id || currentUser?.auth?.user_id;

  useEffect(() => {
    loadUsers();
    loadFamilies();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/api/ft/admin/users');
      const usersList = response.data.users || [];
      setUsers(usersList);
      
      const familiesMap = {};
      for (const user of usersList) {
        try {
          const familiesRes = await familiesAPI.list();
          const allFamilies = familiesRes.data.families || [];
          const userFamilyIds = [];
          
          for (const family of allFamilies) {
            try {
              const membershipsRes = await membershipsAPI.list(family.id);
              const memberships = membershipsRes.data.memberships || [];
              if (memberships.some(m => m.user_id === user.id)) {
                userFamilyIds.push(family.id);
              }
            } catch (e) {
              // Skip if can't access membership
            }
          }
          familiesMap[user.id] = userFamilyIds;
        } catch (e) {
          familiesMap[user.id] = [];
        }
      }
      setUserFamilies(familiesMap);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadFamilies = async () => {
    try {
      const response = await familiesAPI.list();
      setFamilies(response.data.families || []);
    } catch (err) {
      console.error('Failed to load families:', err);
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setEditForm({
      full_name: user.full_name || '',
      is_admin: user.is_admin || false,
    });
    setEditing(true);
  };

  const handleSave = async () => {
    try {
      setError('');
      await api.patch(`/api/ft/admin/users/${selectedUser.id}`, editForm);
      await loadUsers();
      setEditing(false);
      setSelectedUser(null);
    } catch (err) {
      console.error('Failed to update user:', err);
      setError(err.response?.data?.error || 'Failed to update user');
    }
  };

  const handleDelete = async (userId, email) => {
    if (!window.confirm(`Are you sure you want to delete user ${email}? This action cannot be undone.`)) {
      return;
    }

    try {
      setError('');
      await api.delete(`/api/ft/admin/users/${userId}`);
      await loadUsers();
    } catch (err) {
      console.error('Failed to delete user:', err);
      setError(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const toggleAdmin = async (user) => {
    try {
      setError('');
      await api.patch(`/api/ft/admin/users/${user.id}`, {
        is_admin: !user.is_admin,
      });
      await loadUsers();
    } catch (err) {
      console.error('Failed to update user:', err);
      setError(err.response?.data?.error || 'Failed to update user');
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '32px 0', textAlign: 'center' }}>
        <p style={{ color: theme.text.secondary }}>Loading users...</p>
      </div>
    );
  }

  const totalUsers = users.length;
  const adminUsers = users.filter(u => u.is_admin).length;
  const regularUsers = totalUsers - adminUsers;

  return (
    <div>
      {/* User Statistics */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '24px',
      }}>
        <GlassCard style={{ padding: '20px', textAlign: 'center' }}>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: theme.accent.primary, marginBottom: '8px' }}>
            {totalUsers}
          </div>
          <div style={{ color: theme.text.secondary, fontSize: '14px' }}>Total Users</div>
        </GlassCard>
        <GlassCard style={{ padding: '20px', textAlign: 'center' }}>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#ff6b6b', marginBottom: '8px' }}>
            {adminUsers}
          </div>
          <div style={{ color: theme.text.secondary, fontSize: '14px' }}>Admin Users</div>
        </GlassCard>
        <GlassCard style={{ padding: '20px', textAlign: 'center' }}>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: theme.text.primary, marginBottom: '8px' }}>
            {regularUsers}
          </div>
          <div style={{ color: theme.text.secondary, fontSize: '14px' }}>Regular Users</div>
        </GlassCard>
      </div>

      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        gap: '16px',
        justifyContent: 'space-between', 
        alignItems: 'stretch', 
        marginBottom: '20px' 
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <h2 style={{ fontSize: 'clamp(20px, 4vw, 24px)', margin: 0, color: theme.text.primary }}>
            👤 User Management
          </h2>
          <button
            onClick={loadUsers}
            style={{
              padding: '12px 16px',
              borderRadius: '8px',
              border: theme.glass.border,
              background: theme.card.background,
              color: theme.text.primary,
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 500,
              minHeight: '44px',
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          padding: '12px',
          borderRadius: '8px',
          background: 'rgba(255, 0, 0, 0.1)',
          border: '1px solid rgba(255, 0, 0, 0.3)',
          color: '#ff4444',
          marginBottom: '16px',
          fontSize: '14px',
        }}>
          {error}
        </div>
      )}

      {editing && selectedUser && (
        <GlassCard style={{ marginBottom: '24px', padding: 'clamp(16px, 4vw, 24px)', background: theme.card.background }}>
          <h3 style={{ fontSize: 'clamp(18px, 4vw, 20px)', marginBottom: '16px', color: theme.text.primary, wordBreak: 'break-word' }}>
            Edit User: {selectedUser.email}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', color: theme.text.secondary, fontSize: '14px' }}>
                Full Name
              </label>
              <input
                type="text"
                value={editForm.full_name}
                onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: theme.glass.border,
                  background: theme.background,
                  color: theme.text.primary,
                  fontSize: '16px',
                  minHeight: '44px',
                }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minHeight: '44px' }}>
              <input
                type="checkbox"
                id="is_admin"
                checked={editForm.is_admin}
                onChange={(e) => setEditForm({ ...editForm, is_admin: e.target.checked })}
                style={{ width: '20px', height: '20px', cursor: 'pointer', minWidth: '20px', minHeight: '20px' }}
              />
              <label htmlFor="is_admin" style={{ color: theme.text.secondary, fontSize: '16px', cursor: 'pointer' }}>
                Admin Role
              </label>
            </div>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button
                onClick={handleSave}
                style={{
                  padding: '12px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: theme.accent.primary,
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 600,
                  minHeight: '44px',
                  flex: 1,
                  minWidth: '120px',
                }}
              >
                Save Changes
              </button>
              <button
                onClick={() => {
                  setEditing(false);
                  setSelectedUser(null);
                  setError('');
                }}
                style={{
                  padding: '12px 20px',
                  borderRadius: '8px',
                  border: theme.glass.border,
                  background: 'transparent',
                  color: theme.text.secondary,
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 600,
                  minHeight: '44px',
                  flex: 1,
                  minWidth: '120px',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </GlassCard>
      )}

      {/* Mobile Card View */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {users.map((user) => (
          <GlassCard 
            key={user.id}
            style={{ 
              padding: 'clamp(16px, 4vw, 20px)',
              background: theme.card.background,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 'clamp(16px, 4vw, 18px)', fontWeight: 600, color: theme.text.primary, marginBottom: '4px', wordBreak: 'break-word' }}>
                    {user.full_name || user.email}
                  </div>
                  <div style={{ fontSize: 'clamp(13px, 3vw, 14px)', color: theme.text.secondary, wordBreak: 'break-all' }}>
                    {user.email}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{
                    padding: '6px 10px',
                    borderRadius: '6px',
                    background: user.provider === 'local' ? theme.accent.primary + '20' : theme.text.muted + '20',
                    color: user.provider === 'local' ? theme.accent.primary : theme.text.secondary,
                    fontSize: 'clamp(11px, 2.5vw, 12px)',
                    textTransform: 'capitalize',
                    whiteSpace: 'nowrap',
                  }}>
                    {user.provider || 'google'}
                  </span>
                  {user.is_admin ? (
                    <span style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      background: '#ff6b6b20',
                      color: '#ff6b6b',
                      fontSize: 'clamp(11px, 2.5vw, 12px)',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}>
                      Admin
                    </span>
                  ) : (
                    <span style={{
                      padding: '6px 10px',
                      borderRadius: '6px',
                      background: theme.text.muted + '20',
                      color: theme.text.secondary,
                      fontSize: 'clamp(11px, 2.5vw, 12px)',
                      whiteSpace: 'nowrap',
                    }}>
                      User
                    </span>
                  )}
                </div>
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 140px), 1fr))', 
                gap: '12px',
                paddingTop: '12px',
                borderTop: theme.glass.border,
              }}>
                <div>
                  <div style={{ fontSize: 'clamp(11px, 2.5vw, 12px)', color: theme.text.secondary, marginBottom: '4px' }}>
                    Created
                  </div>
                  <div style={{ fontSize: 'clamp(13px, 3vw, 14px)', color: theme.text.primary }}>
                    {user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 'clamp(11px, 2.5vw, 12px)', color: theme.text.secondary, marginBottom: '4px' }}>
                    Last Login
                  </div>
                  <div style={{ fontSize: 'clamp(13px, 3vw, 14px)', color: theme.text.primary }}>
                    {user.last_login_at ? new Date(user.last_login_at).toLocaleDateString() : 'Never'}
                  </div>
                </div>
              </div>

              <div style={{ paddingTop: '12px', borderTop: theme.glass.border }}>
                <div style={{ fontSize: 'clamp(11px, 2.5vw, 12px)', color: theme.text.secondary, marginBottom: '8px' }}>
                  Family Access
                </div>
                {user.is_admin ? (
                  <div style={{ fontSize: 'clamp(13px, 3vw, 14px)', fontWeight: 600, color: theme.accent.primary }}>
                    All families (Admin)
                  </div>
                ) : userFamilies[user.id] && userFamilies[user.id].length > 0 ? (
                  <div>
                    <div style={{ fontSize: 'clamp(13px, 3vw, 14px)', fontWeight: 600, color: theme.text.primary, marginBottom: '8px' }}>
                      {userFamilies[user.id].length} {userFamilies[user.id].length === 1 ? 'family' : 'families'}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {userFamilies[user.id].map((familyId) => {
                        const family = families.find(f => f.id === familyId);
                        return family ? (
                          <span
                            key={familyId}
                            style={{
                              padding: '4px 8px',
                              borderRadius: '4px',
                              background: theme.accent.primary + '20',
                              color: theme.accent.primary,
                              fontSize: 'clamp(11px, 2.5vw, 12px)',
                              wordBreak: 'break-word',
                            }}
                          >
                            {family.name}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </div>
                ) : (
                  <span style={{ fontSize: 'clamp(13px, 3vw, 14px)', color: theme.text.muted }}>No access</span>
                )}
              </div>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(2, 1fr)', 
                gap: '8px',
                paddingTop: '12px',
                borderTop: theme.glass.border,
              }}>
                <button
                  onClick={() => handleEdit(user)}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: theme.glass.border,
                    background: 'transparent',
                    color: theme.text.primary,
                    cursor: 'pointer',
                    fontSize: 'clamp(14px, 3vw, 16px)',
                    fontWeight: 500,
                    minHeight: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                  }}
                >
                  ✏️ Edit
                </button>
                <button
                  onClick={() => setShowFamilyAccess(showFamilyAccess === user.id ? null : user.id)}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: theme.glass.border,
                    background: showFamilyAccess === user.id ? theme.accent.primary + '20' : 'transparent',
                    color: showFamilyAccess === user.id ? theme.accent.primary : theme.text.primary,
                    cursor: 'pointer',
                    fontSize: 'clamp(14px, 3vw, 16px)',
                    fontWeight: 500,
                    minHeight: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                  }}
                >
                  👨‍👩‍👧‍👦 Families
                </button>
                <button
                  onClick={() => toggleAdmin(user)}
                  disabled={user.id === currentUserId}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: theme.glass.border,
                    background: user.is_admin ? 'rgba(255, 107, 107, 0.1)' : 'rgba(76, 175, 80, 0.1)',
                    color: user.is_admin ? '#ff6b6b' : '#4caf50',
                    cursor: user.id === currentUserId ? 'not-allowed' : 'pointer',
                    fontSize: 'clamp(14px, 3vw, 16px)',
                    fontWeight: 500,
                    opacity: user.id === currentUserId ? 0.5 : 1,
                    minHeight: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                  }}
                >
                  {user.is_admin ? '👑 Remove Admin' : '⭐ Make Admin'}
                </button>
                <button
                  onClick={() => handleDelete(user.id, user.email)}
                  disabled={user.id === currentUserId}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: theme.glass.border,
                    background: 'rgba(255, 0, 0, 0.1)',
                    color: '#ff4444',
                    cursor: user.id === currentUserId ? 'not-allowed' : 'pointer',
                    fontSize: 'clamp(14px, 3vw, 16px)',
                    fontWeight: 500,
                    opacity: user.id === currentUserId ? 0.5 : 1,
                    minHeight: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                  }}
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          </GlassCard>
        ))}
      </div>

      {users.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px', color: theme.text.muted }}>
          No users found
        </div>
      )}

      {/* Family Access Management */}
      {showFamilyAccess && users.find(u => u.id === showFamilyAccess) && (
        <GlassCard style={{ marginTop: '24px', padding: '24px', background: theme.card.background }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '20px', margin: 0, color: theme.text.primary }}>
              Family Access: {users.find(u => u.id === showFamilyAccess)?.email}
            </h3>
            <button
              onClick={() => setShowFamilyAccess(null)}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: theme.glass.border,
                background: 'transparent',
                color: theme.text.secondary,
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              ✕
            </button>
          </div>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {families.map((family) => {
              const hasAccess = userFamilies[showFamilyAccess]?.includes(family.id) || false;
              return (
                <div
                  key={family.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '12px',
                    borderRadius: '8px',
                    border: theme.glass.border,
                    background: hasAccess ? theme.accent.primary + '10' : theme.background,
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 600, color: theme.text.primary, marginBottom: '4px' }}>
                      {family.name}
                    </div>
                    <div style={{ fontSize: '12px', color: theme.text.secondary }}>
                      {family.member_count || 0} members
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        if (hasAccess) {
                          await membershipsAPI.delete(family.id, showFamilyAccess);
                        } else {
                          await membershipsAPI.upsert(family.id, showFamilyAccess, {
                            role: 'viewer',
                            status: 'active',
                          });
                        }
                        await loadUsers();
                      } catch (err) {
                        setError(err.response?.data?.error || 'Failed to update family access');
                      }
                    }}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '6px',
                      border: 'none',
                      background: hasAccess ? '#ff4444' : theme.accent.primary,
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: 600,
                    }}
                  >
                    {hasAccess ? 'Remove Access' : 'Grant Access'}
                  </button>
                </div>
              );
            })}
            
            {families.length === 0 && (
              <div style={{ textAlign: 'center', padding: '24px', color: theme.text.muted }}>
                No families available
              </div>
            )}
          </div>
        </GlassCard>
      )}
    </div>
  );
};

export default UserManagement;
